import { ChevronRightSharp } from '@mui/icons-material';
import { Accordion, AccordionDetails, Box, Button, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFetchData } from '../../../../hooks/useFetchData';
import { getKeyQuestionRelevantDocuments } from '../../../../services/dealGPT';
import { StyledAccordionSummary } from '../../../../theme/CustomComponents';
import type { ProjectConfig } from '../../../../Types/dealGPT';
import { DealGPTView } from '../../../../Types/enums';
import { SourceDocument } from '../../../../Types/indexes';
import { projectConfigPathUrl } from '../../../../util/routesUrls';
import ReferenceDocuments from '../ReferenceDocuments';
import { DownloadKeyQuestionSummaryButton } from './DownloadKeyQuestionSummaryButton';

interface KeyQuestionsSummaryProps {
  projectId: string;
  runId: string;
  runScopes: string[];
}

export const KeyQuestionsSummary: React.FC<KeyQuestionsSummaryProps> = ({
  projectId,
  runId,
  runScopes,
}) => {
  const { data: projectConfig } = useFetchData<ProjectConfig>({
    urlPath: projectConfigPathUrl(projectId),
  });

  const keyQuestions = useMemo(() => projectConfig?.keyQuestions || [], [projectConfig]);

  // Mapping of question IDs to their expanded state
  const [expanded, setExpanded] = useState<Record<string, boolean>>(
    keyQuestions.reduce<Record<string, boolean>>((acc, question) => {
      question.id && (acc[question.id] = false);
      return acc;
    }, {})
  );
  // Mapping of question IDs to their relevant documents
  const [documentsMap, setDocumentsMap] = useState<Record<string, SourceDocument[]>>({});

  // Check if all questions are expanded
  const allExpanded = useMemo(
    () => Object.values(expanded).every((isExpanded) => isExpanded),
    [expanded]
  );

  // Toggle expand/collapse all questions
  const toggleExpandAll = useCallback(() => {
    const expandedState = keyQuestions.reduce<Record<string, boolean>>((acc, question) => {
      question.id && (acc[question.id] = !allExpanded);
      return acc;
    }, {});
    setExpanded(expandedState);
  }, [keyQuestions, allExpanded]);

  // Fetch relevant documents for each question
  useEffect(() => {
    if (keyQuestions.length === 0) return;

    const fetchSourceDocuments = async () => {
      const documentsResults = await Promise.all(
        keyQuestions.map(async (question) => ({
          question,
          documents: await getKeyQuestionRelevantDocuments(projectId, question.id as string, runId),
        }))
      );

      const mapState = documentsResults.reduce<Record<string, SourceDocument[]>>(
        (acc, { question, documents }) => {
          acc[question.id as string] = documents;
          return acc;
        },
        {}
      );

      setDocumentsMap(mapState);
    };

    fetchSourceDocuments();
  }, [keyQuestions, projectId, runId]);

  if (keyQuestions.length === 0)
    return <Typography>No key questions found for this run.</Typography>;

  return (
    <Box display="flex" flexDirection="column" marginBottom={'200px'} gap={1}>
      <DownloadKeyQuestionSummaryButton
        keyQuestions={keyQuestions}
        documentsMap={documentsMap}
        runId={runId}
        runScopes={runScopes}
      />
      <Box display="flex" justifyContent="end">
        <Button variant="text" onClick={toggleExpandAll}>
          {allExpanded ? 'Collapse All' : 'Expand All'}
        </Button>
      </Box>
      <Box>
        {keyQuestions.map((question) => (
          <Accordion
            key={question.id}
            expanded={expanded[question.id as string]}
            onChange={(_, isExpanded) =>
              setExpanded((prev) => ({ ...prev, [question.id as string]: isExpanded }))
            }
          >
            <StyledAccordionSummary expandIcon={<ChevronRightSharp />}>
              <Typography variant="subtitle2">{question.question}</Typography>
            </StyledAccordionSummary>
            <AccordionDetails>
              <Box padding={2}>
                {documentsMap[question.id as string]?.length > 0 ? (
                  <ReferenceDocuments
                    questionId={question.id as string}
                    view={DealGPTView.DISCOVERY}
                    showAnalysisText={false}
                    documents={documentsMap[question.id as string]}
                    showAll={true}
                  />
                ) : (
                  <Typography>
                    No relevant documents found, have you ran a discovery scan yet?
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};
